import type { ConvexReactClient } from "convex/react";

/**
 * client/src/lib/convexClient.ts
 *
 * Module-level handle on the singleton ConvexReactClient created in main.tsx.
 *
 * Why this exists: snapshotSync and telemetry are imperative modules called
 * from effects and `pagehide` listeners, not React components, so they cannot
 * use `useConvex()`. They need the same authenticated client the React tree
 * uses -- a second client would carry no auth token.
 *
 * main.tsx calls setConvexClient(convex) before render.
 */

let client: ConvexReactClient | null = null;

export function setConvexClient(next: ConvexReactClient | null): void {
  client = next;
}

export function getConvexClient(): ConvexReactClient | null {
  return client;
}

/**
 * Get the client or throw. Use this on any path where a missing client means
 * the operation genuinely cannot happen -- callers must not treat a null
 * client as "succeeded". A null client in production means VITE_CONVEX_URL
 * was not set at build time, which is a deploy misconfiguration, not a
 * runtime condition to be swallowed.
 */
export function requireConvexClient(): ConvexReactClient {
  if (!client) {
    throw new Error(
      "[convex] client unavailable — VITE_CONVEX_URL was not set at build time",
    );
  }
  return client;
}
